import { observer } from "mobx-react-lite";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { CustomModelFormValues } from "./types";
import { useCustomModelsForm } from "./hooks/useCustomModelsForm";
import { useCustomParametersStore } from "./CustomModelsCreation/CustomParameters/custom-parameters-store";
import { ConfirmExitDialog } from "./ConfirmExitDialog";
import { canContinueCustomModelStep1, getCustomModelCreationSteps } from "./CustomModelsCreation";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Stepper } from "@/components/Stepper";
import { cn } from "@/lib/utils";
import { useProviders } from "@/queries/providers/useProviders";
import { useAuth } from "@/context/auth-context";
import { Provider, useModels } from "@/queries/models";

interface CustomModelDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  providerId?: number;
  setIsConfirmationModalOpen: (isConfirmationModalOpen: boolean) => void;
  isConfirmationModalOpen: boolean;
  defaultValues: {
    provider?: string;
    modelName?: string;
    displayName?: string;
    modelType?: string;
    baseModel?: string;
    apiKey?: string;
    id?: number;
  };
}

export const CustomModelDialogContent = observer(
  ({
    isOpen,
    onOpenChange,
    setIsConfirmationModalOpen,
    isConfirmationModalOpen,
    defaultValues,
  }: Omit<CustomModelDialogProps, "providerId">) => {
    const store = useCustomParametersStore();
    const authContext = useAuth();
    const { data: models } = useModels({
      providerId: store.providerId,
    });
    const [activeStep, setActiveStep] = useState(0);
    const [isNavigatingBack, setIsNavigatingBack] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState<number>();
    const methods = useForm<CustomModelFormValues>({
      mode: "onChange",
      defaultValues: {
        create_custom_model: {
          provider: "",
          modelName: "",
          displayName: "",
          modelType: "chat",
          baseModel: "",
          apiKey: "",
          id: undefined,
        },
      },
    });

    const { data: providers } = useProviders({
      userToken: authContext?.userToken ?? "",
    });

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      methods.reset({
        create_custom_model: {
          provider: defaultValues.provider ?? "",
          modelName: defaultValues.modelName ?? "",
          displayName: defaultValues.displayName ?? "",
          modelType: defaultValues.modelType ?? "chat",
          baseModel: defaultValues.baseModel ?? "",
          apiKey: defaultValues.apiKey ?? "",
          id: defaultValues.id ?? undefined,
        },
      });
    }, [defaultValues, isOpen, methods]);

    const { isSubmitted, setIsSubmitted, onSubmit, isLoading, reset } = useCustomModelsForm({
      setIsOpen: onOpenChange,
      methods,
      setActiveStep,
    });

    const watchedValues = useWatch({
      control: methods.control,
      name: "create_custom_model",
    });

    const steps = useMemo(
      () =>
        getCustomModelCreationSteps({
          models: models?.model_configs || [],
          providers: providers || [],
          isEdit: store.isEditing,
          isViewing: store.isViewing,
          isLoading,
          isStep1NextDisabled: !canContinueCustomModelStep1({
            values: watchedValues,
            models: models?.model_configs || [],
            isEdit: store.isEditing,
            isViewing: store.isViewing,
          }),
        }),
      [models, store.isEditing, store.isViewing, isLoading, providers, watchedValues],
    );

    useEffect(() => {
      if (watchedValues.provider) {
        const provider = providers.find((p: Provider) => p.name === watchedValues.provider);

        if (provider) {
          store.setProviderId(provider.id);
        }
      }
    }, [providers, store, watchedValues.provider]);

    useLayoutEffect(() => {
      if (!isOpen) {
        setContentHeight(undefined);
        return;
      }

      const node = contentRef.current;

      if (!node) {
        return;
      }

      const updateHeight = () => {
        setContentHeight(node.scrollHeight);
      };

      updateHeight();

      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(node);

      return () => resizeObserver.disconnect();
    }, [
      isOpen,
      activeStep,
      watchedValues.provider,
      watchedValues.modelType,
      store.isViewing,
      isLoading,
    ]);

    const handleDialogOpenChange = (open: boolean) => {
      if (!open && !store.isViewing && store.areParametersDifferent) {
        setIsNavigatingBack(false);
        setIsConfirmationModalOpen(true);
        return;
      }

      onOpenChange(open);

      if (!open) {
        setIsNavigatingBack(false);
        methods.reset();
        setActiveStep(0);
        store.reset();
        if (store.isViewing) {
          store.setIsViewing(false);
        } else {
          store.setIsEditing(false);
        }
      }
    };

    const handleBack = () => {
      if (activeStep === 1 && !store.isViewing && store.areParametersDifferent) {
        setIsNavigatingBack(true);
        setIsConfirmationModalOpen(true);
        return;
      }

      setActiveStep(activeStep - 1);
    };

    const handleDiscardBack = () => {
      store.restoreSavedParameters();
      setActiveStep(0);
      setIsNavigatingBack(false);
      setIsConfirmationModalOpen(false);
    };

    const handleNext = () => {
      if (activeStep === 0) {
        // Baseline step 2 against current params so Back only warns for
        // changes made on the parameters step.
        store.captureSavedParametersSnapshot();
      }

      setActiveStep(activeStep + 1);
    };

    return (
      <>
        <ConfirmExitDialog
          reset={reset}
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
          setIsConfirmationModalOpen={open => {
            setIsConfirmationModalOpen(open);
            if (!open) {
              setIsNavigatingBack(false);
            }
          }}
          isConfirmationModalOpen={isConfirmationModalOpen}
          isNavigatingBack={isNavigatingBack}
          onDiscardBack={handleDiscardBack}
        />
        <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
          <DialogContent
            className={cn(
              "grid items-start overflow-hidden transition-[width,max-width,height] duration-300 ease-in-out",
              activeStep > 0 ? "max-w-screen-md" : "max-w-lg",
            )}
            style={contentHeight !== undefined ? { height: contentHeight + 48 } : undefined}
          >
            <div ref={contentRef} className="w-full">
              <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  <Stepper
                    size="dialog"
                    activeStep={activeStep}
                    steps={steps}
                    isPreview={store.isViewing}
                    onNext={handleNext}
                    onBack={handleBack}
                    onExit={() => {
                      handleDialogOpenChange(false);
                    }}
                  />
                </form>
              </FormProvider>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);
